<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { getModifierKey } from 'dashboard/composables/utils/useKbd';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { stripWahaSendPrefix } from 'dashboard/helper/wahaSendPrefix';
import { waitingLabelFor } from 'dashboard/helper/waitingLabel';
import { conversationViewComparator } from 'dashboard/store/modules/conversations/helpers';
import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';
import Avatar from 'next/avatar/Avatar.vue';

const QUEUE_PREVIEW_SIZE = 5;

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { accountScopedRoute } = useAccount();

const searchInput = ref(null);
const accountId = useMapGetter('getCurrentAccountId');
const conversations = useMapGetter('getFilteredConversations');

const shortcutLabel = computed(() =>
  t('CONVERSATION.EMPTY_STATE.SHORTCUT', { mod: getModifierKey() })
);

const hubRoute = computed(() => {
  if (!isWorkspaceV2Path(route.path)) return accountScopedRoute('focus_hub');
  const fromWorldId =
    typeof route.query?.from_world === 'string'
      ? worldById(route.query.from_world)?.id
      : undefined;

  return {
    path: workspaceV2Path(`/app/accounts/${route.params.accountId}/hub`),
    ...(fromWorldId ? { query: { from_world: fromWorldId } } : {}),
  };
});

// Mesmo comparador da fila do Modo Foco: prioriza quem aguarda resposta,
// dentro da janela de 24h do WhatsApp primeiro, por waiting_since ascendente.
const queueComparator = conversationViewComparator('waiting_window');

const queuePreview = computed(() =>
  [...(conversations.value || [])]
    .sort(queueComparator)
    .slice(0, QUEUE_PREVIEW_SIZE)
);

const contactOf = chat => {
  const sender = chat?.meta?.sender || {};
  if (sender.id) {
    const stored = store.getters['contacts/getContact'](sender.id);
    if (stored?.name || stored?.thumbnail) return stored;
  }
  return sender;
};

const previewOf = chat => {
  const fromApi =
    chat?.last_non_activity_message?.content ||
    chat?.lastNonActivityMessage?.content;
  if (fromApi) return stripWahaSendPrefix(fromApi);

  if (Array.isArray(chat?.messages) && chat.messages.length) {
    const last = getLastMessage(chat);
    if (last?.content) return stripWahaSendPrefix(last.content);
  }

  return t('CHAT_LIST.NO_CONTENT');
};

const waitLabelOf = chat => waitingLabelFor(chat, t);

const goToSearch = (query = '') => {
  router.push(accountScopedRoute('search', {}, query ? { q: query } : {}));
};

const onSearchKeydown = event => {
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  if (
    [
      'Tab',
      'Shift',
      'Escape',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
    ].includes(event.key)
  ) {
    return;
  }

  event.preventDefault();
  const seed = event.key.length === 1 ? event.key : '';
  goToSearch(seed);
};

const openConversation = chat => {
  if (!chat?.id) return;
  const path = frontendURL(
    conversationUrl({ accountId: accountId.value, id: chat.id })
  );
  router.push({ path });
};

onMounted(() => {
  const activeTag = document.activeElement?.tagName;
  const nothingElseFocused = !activeTag || activeTag === 'BODY';
  if (nothingElseFocused) {
    searchInput.value?.focus();
  }
});
</script>

<template>
  <div
    class="flex h-full w-full flex-col items-center overflow-auto bg-n-background px-6 py-16"
  >
    <div class="flex w-full max-w-xl flex-col items-stretch">
      <label
        class="flex min-h-[68px] items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-1 px-5 focus-within:border-n-brand"
        @click="goToSearch()"
      >
        <span
          class="i-ri-search-line size-5 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        <input
          ref="searchInput"
          type="search"
          class="min-w-0 flex-1 bg-transparent text-[17px] font-bold text-n-slate-12 outline-none placeholder:font-bold placeholder:text-n-slate-10"
          :placeholder="t('COMMAND_BAR.SEARCH_PLACEHOLDER')"
          :aria-label="t('COMMAND_BAR.SEARCH_PLACEHOLDER')"
          autocomplete="off"
          @click="goToSearch()"
          @keydown="onSearchKeydown"
        />
        <kbd
          class="inline-flex min-h-6 min-w-[26px] items-center justify-center rounded-md border border-n-weak bg-n-background px-1.5 text-[12px] font-bold tabular-nums text-n-slate-11"
        >
          {{ shortcutLabel }}
        </kbd>
      </label>

      <section v-if="queuePreview.length" class="mt-10">
        <h2 class="m-0 mb-3 text-[13px] font-bold text-n-slate-10">
          {{ t('CONVERSATION.EMPTY_STATE.QUEUE_TITLE') }}
        </h2>
        <ul class="m-0 flex list-none flex-col gap-1 p-0">
          <li v-for="chat in queuePreview" :key="chat.id">
            <button
              type="button"
              class="flex min-h-11 w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors duration-150 hover:bg-n-solid-2"
              @click="openConversation(chat)"
            >
              <Avatar
                :name="contactOf(chat).name || ''"
                :src="contactOf(chat).thumbnail || ''"
                :size="40"
                rounded-full
              />
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-[15px] font-bold text-n-slate-12"
                >
                  {{ contactOf(chat).name }}
                </span>
                <span class="mt-0.5 block truncate text-[13px] text-n-slate-11">
                  {{ previewOf(chat) }}
                </span>
              </span>
              <span
                v-if="waitLabelOf(chat)"
                class="shrink-0 text-[12px] tabular-nums text-n-slate-10"
              >
                {{ waitLabelOf(chat) }}
              </span>
            </button>
          </li>
        </ul>
      </section>

      <section v-else class="mt-12 flex flex-col items-center text-center">
        <p class="m-0 text-[15px] font-bold text-n-slate-12">
          {{ t('CHAT_LIST.FOCUS_QUEUE.EMPTY_TITLE') }}
        </p>
        <p class="m-0 mt-1 text-[13px] text-n-slate-11">
          {{ t('CHAT_LIST.FOCUS_QUEUE.EMPTY_BODY') }}
        </p>
        <router-link
          :to="hubRoute"
          class="mt-4 inline-flex min-h-11 items-center rounded-full px-4 text-[14px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
        >
          {{ t('CHAT_LIST.FOCUS_QUEUE.EMPTY_BACK_HUB') }}
        </router-link>
      </section>
    </div>
  </div>
</template>
